/**
 * Status Settings Page
 *
 * Manage tenant-specific task statuses with inline editing.
 * - Color customization
 * - Labels and ordering
 * - Default and terminal status flags
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import {
  Typography,
  Input,
  ColorPicker,
  Popconfirm,
  Spin,
  Empty,
  Tooltip,
  Switch,
  Tag,
  Space,
  message,
} from 'antd';
import {
  DeleteOutlined,
  PlusOutlined,
  CheckCircleOutlined,
  SyncOutlined,
  ExclamationCircleOutlined,
  HolderOutlined,
  StarOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { statusesApi, TenantStatus, UpdateStatusRequest } from '@api/statuses';
import { useAutoSave } from '@hooks/useAutoSave';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const { Title, Text } = Typography;

interface EditableStatus extends TenantStatus {
  isNew?: boolean;
}

// Sortable status row
function SortableStatusRow(props: {
  status: EditableStatus;
  onUpdate: (id: string, data: UpdateStatusRequest) => Promise<void>;
  onDelete: (id: string) => void;
  onCreated?: (status: TenantStatus) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: props.status.id,
    disabled: props.status.isNew,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 'auto',
  };

  return (
    <div ref={setNodeRef} style={style}>
      <StatusRow {...props} dragHandleProps={{ ...attributes, ...listeners }} />
    </div>
  );
}

// Individual status row with inline editing
function StatusRow({
  status,
  onUpdate,
  onDelete,
  onCreated,
  dragHandleProps,
}: {
  status: EditableStatus;
  onUpdate: (id: string, data: UpdateStatusRequest) => Promise<void>;
  onDelete: (id: string) => void;
  onCreated?: (status: TenantStatus) => void;
  dragHandleProps?: Record<string, unknown>;
}) {
  const [key, setKey] = useState(status.key);
  const [label, setLabel] = useState(status.label);
  const [color, setColor] = useState(status.color);
  const [bgColor, setBgColor] = useState(status.bgColor || status.color + '20');
  const [isDefault, setIsDefault] = useState(status.isDefault);
  const [isTerminal, setIsTerminal] = useState(status.isTerminal);
  const [isActive, setIsActive] = useState(status.isActive);
  const [isCreating, setIsCreating] = useState(false);
  const inputRef = useRef<any>(null);
  const queryClient = useQueryClient();

  // Auto-save for updates
  const { save, status: saveStatus } = useAutoSave({
    saveFn: async (data: UpdateStatusRequest) => {
      await onUpdate(status.id, data);
    },
    queryKey: 'statuses',
    debounceMs: 600,
  });

  // Create mutation for new statuses
  const createMutation = useMutation({
    mutationFn: async (data: { key: string; label: string; color: string; bg_color?: string; is_terminal?: boolean }) => {
      return statusesApi.create(data);
    },
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['statuses'] });
      onCreated?.(created);
      setKey('');
      setLabel('');
      setColor('#8c8c8c');
      setBgColor('#f0f0f0');
      setIsCreating(false);
    },
    onError: (err: any) => {
      message.error(err.response?.data?.error || 'Failed to create status');
      setIsCreating(false);
    },
  });

  // Focus input for new rows
  useEffect(() => {
    if (status.isNew && inputRef.current) {
      inputRef.current.focus();
    }
  }, [status.isNew]);

  const handleKeyChange = useCallback((value: string) => {
    // Only lowercase and underscores
    const sanitized = value.toLowerCase().replace(/[^a-z_]/g, '');
    setKey(sanitized);
  }, []);

  const handleLabelChange = useCallback(
    (value: string) => {
      setLabel(value);
      if (!status.isNew && value.trim()) {
        save({ label: value });
      }
    },
    [status.isNew, save]
  );

  const handleColorChange = useCallback(
    (newColor: any) => {
      const hexColor = typeof newColor === 'string' ? newColor : newColor?.toHexString?.() || '#8c8c8c';
      setColor(hexColor);
      if (!status.isNew) {
        save({ color: hexColor });
      }
    },
    [status.isNew, save]
  );

  const handleBgColorChange = useCallback(
    (newColor: any) => {
      const hexColor = typeof newColor === 'string' ? newColor : newColor?.toHexString?.() || '#f0f0f0';
      setBgColor(hexColor);
      if (!status.isNew) {
        save({ bg_color: hexColor });
      }
    },
    [status.isNew, save]
  );

  const handleDefaultChange = useCallback(
    (checked: boolean) => {
      setIsDefault(checked);
      if (!status.isNew) {
        save({ is_default: checked });
      }
    },
    [status.isNew, save]
  );

  const handleTerminalChange = useCallback(
    (checked: boolean) => {
      setIsTerminal(checked);
      if (!status.isNew) {
        save({ is_terminal: checked });
      }
    },
    [status.isNew, save]
  );

  const handleActiveChange = useCallback(
    (checked: boolean) => {
      setIsActive(checked);
      if (!status.isNew) {
        save({ is_active: checked });
      }
    },
    [status.isNew, save]
  );

  const handleCreate = useCallback(() => {
    if (key.trim() && label.trim() && !isCreating) {
      setIsCreating(true);
      createMutation.mutate({
        key: key.trim(),
        label: label.trim(),
        color,
        bg_color: bgColor,
        is_terminal: isTerminal,
      });
    }
  }, [key, label, color, bgColor, isTerminal, isCreating, createMutation]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && status.isNew) {
        handleCreate();
      }
    },
    [status.isNew, handleCreate]
  );

  // Status indicator
  const StatusIcon = () => {
    if (isCreating || createMutation.isPending) {
      return <SyncOutlined spin style={{ color: '#1890ff', fontSize: 12 }} />;
    }
    switch (saveStatus) {
      case 'pending':
      case 'saving':
        return <SyncOutlined spin style={{ color: '#1890ff', fontSize: 12 }} />;
      case 'saved':
        return <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 12 }} />;
      case 'error':
        return <ExclamationCircleOutlined style={{ color: '#ff4d4f', fontSize: 12 }} />;
      default:
        return null;
    }
  };

  // Core statuses can't be deleted
  const isCoreStatus = ['pending', 'in_progress', 'completed'].includes(status.key);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '8px 0',
        borderBottom: '1px solid #f0f0f0',
        backgroundColor: !isActive && !status.isNew ? '#fafafa' : 'transparent',
      }}
    >
      {/* Drag handle */}
      {!status.isNew && (
        <div {...dragHandleProps} style={{ cursor: 'grab', color: '#999' }}>
          <HolderOutlined />
        </div>
      )}
      {status.isNew && <div style={{ width: 14 }} />}

      {/* Color dot with picker */}
      <ColorPicker
        value={color}
        onChange={handleColorChange}
        size="small"
        presets={[
          {
            label: 'Colors',
            colors: [
              '#8c8c8c', '#1890ff', '#52c41a', '#faad14', '#ff4d4f',
              '#722ed1', '#eb2f96', '#13c2c2', '#fa8c16', '#bfbfbf',
            ],
          },
        ]}
      >
        <div
          style={{
            width: 20,
            height: 20,
            borderRadius: '50%',
            backgroundColor: color,
            cursor: 'pointer',
            border: '2px solid #fff',
            boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
            flexShrink: 0,
          }}
        />
      </ColorPicker>

      {/* Key input (only editable for new) */}
      {status.isNew ? (
        <Input
          ref={inputRef}
          value={key}
          onChange={(e) => handleKeyChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="key_name"
          variant="borderless"
          style={{ width: 100, fontFamily: 'monospace', fontSize: 12 }}
        />
      ) : (
        <Tag style={{ fontFamily: 'monospace', margin: 0, minWidth: 100 }}>{status.key}</Tag>
      )}

      {/* Label input */}
      <Input
        value={label}
        onChange={(e) => handleLabelChange(e.target.value)}
        onBlur={status.isNew ? handleCreate : undefined}
        onKeyDown={handleKeyDown}
        placeholder={status.isNew ? 'Display Label' : 'Label'}
        variant="borderless"
        style={{ flex: 1, maxWidth: 150, fontWeight: 500 }}
      />

      {/* Preview */}
      <div
        style={{
          padding: '2px 8px',
          borderRadius: 4,
          backgroundColor: bgColor,
          borderLeft: `3px solid ${color}`,
          fontSize: 12,
          minWidth: 80,
        }}
      >
        {label || 'Preview'}
      </div>

      {/* Background color picker */}
      <ColorPicker
        value={bgColor}
        onChange={handleBgColorChange}
        size="small"
        presets={[
          {
            label: 'Backgrounds',
            colors: [
              '#f0f0f0', '#e6f7ff', '#f6ffed', '#fffbe6', '#fff2f0',
              '#f9f0ff', '#fff0f6', '#e6fffb', '#fff7e6', '#f5f5f5',
            ],
          },
        ]}
      >
        <Tooltip title="Background color">
          <div
            style={{
              width: 16,
              height: 16,
              borderRadius: 2,
              backgroundColor: bgColor,
              cursor: 'pointer',
              border: '1px solid #d9d9d9',
            }}
          />
        </Tooltip>
      </ColorPicker>

      {/* Default toggle */}
      {!status.isNew && (
        <Tooltip title={isDefault ? 'Default status for new tasks' : 'Set as default'}>
          <Switch
            size="small"
            checked={isDefault}
            onChange={handleDefaultChange}
            checkedChildren={<StarOutlined />}
          />
        </Tooltip>
      )}

      {/* Terminal toggle */}
      {!status.isNew ? (
        <Tooltip title={isTerminal ? 'Terminal state (like completed)' : 'Set as terminal'}>
          <Switch
            size="small"
            checked={isTerminal}
            onChange={handleTerminalChange}
            checkedChildren={<StopOutlined />}
          />
        </Tooltip>
      ) : (
        <Tooltip title="Terminal state (task cannot change after)">
          <Switch
            size="small"
            checked={isTerminal}
            onChange={(checked) => setIsTerminal(checked)}
            checkedChildren={<StopOutlined />}
          />
        </Tooltip>
      )}

      {/* Active toggle */}
      {!status.isNew && (
        <Tooltip title={isActive ? 'Active' : 'Inactive'}>
          <Switch
            size="small"
            checked={isActive}
            onChange={handleActiveChange}
          />
        </Tooltip>
      )}

      {/* Status indicator */}
      <div style={{ width: 20, display: 'flex', justifyContent: 'center' }}>
        <StatusIcon />
      </div>

      {/* Delete button */}
      {!status.isNew && !isCoreStatus && (
        <Popconfirm
          title="Delete status?"
          description="Tasks using this status will keep their current value."
          onConfirm={() => onDelete(status.id)}
          okText="Delete"
          okButtonProps={{ danger: true }}
        >
          <DeleteOutlined
            style={{
              color: '#999',
              cursor: 'pointer',
              padding: 4,
            }}
          />
        </Popconfirm>
      )}

      {/* Placeholder for core statuses */}
      {!status.isNew && isCoreStatus && (
        <Tooltip title="Core status cannot be deleted">
          <DeleteOutlined style={{ color: '#ddd', padding: 4 }} />
        </Tooltip>
      )}

      {/* Add icon for new row */}
      {status.isNew && (
        <PlusOutlined style={{ color: '#999', padding: 4 }} />
      )}
    </div>
  );
}

export default function StatusSettingsPage() {
  const queryClient = useQueryClient();

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Fetch statuses
  const { data: statuses = [], isLoading } = useQuery({
    queryKey: ['statuses', 'all'],
    queryFn: () => statusesApi.list(true), // Include inactive
  });

  // Reorder mutation
  const reorderMutation = useMutation({
    mutationFn: (statusIds: string[]) => statusesApi.reorder(statusIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['statuses'] });
    },
  });

  // Handle drag end
  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (over && active.id !== over.id) {
        const oldIndex = statuses.findIndex((s) => s.id === active.id);
        const newIndex = statuses.findIndex((s) => s.id === over.id);
        const newOrder = arrayMove(statuses, oldIndex, newIndex);
        // Optimistically update UI
        queryClient.setQueryData(['statuses', 'all'], newOrder);
        // Persist to backend
        reorderMutation.mutate(newOrder.map((s) => s.id));
      }
    },
    [statuses, queryClient, reorderMutation]
  );

  // Update mutation
  const updateStatus = useCallback(
    async (id: string, data: UpdateStatusRequest) => {
      await statusesApi.update(id, data);
      queryClient.invalidateQueries({ queryKey: ['statuses'] });
    },
    [queryClient]
  );

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => statusesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['statuses'] });
      message.success('Status deleted');
    },
  });

  const handleDelete = useCallback(
    (id: string) => {
      deleteMutation.mutate(id);
    },
    [deleteMutation]
  );

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 40 }}>
        <Spin />
      </div>
    );
  }

  // Create a "new status" placeholder row
  const newStatusPlaceholder: EditableStatus = {
    id: 'new',
    key: '',
    label: '',
    color: '#8c8c8c',
    bgColor: '#f0f0f0',
    isDefault: false,
    isTerminal: false,
    isActive: true,
    sortOrder: statuses.length,
    createdAt: '',
    updatedAt: '',
    isNew: true,
  };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={4} style={{ margin: 0, marginBottom: 4 }}>
          Task Statuses
        </Title>
        <Text type="secondary">
          Customize the status options available for tasks. Drag to reorder.
        </Text>
      </div>

      {/* Header row */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '8px 0',
          borderBottom: '2px solid #f0f0f0',
          color: '#888',
          fontSize: 12,
          fontWeight: 500,
          textTransform: 'uppercase',
        }}
      >
        <div style={{ width: 14 }}></div>
        <div style={{ width: 20 }}>Color</div>
        <div style={{ width: 100 }}>Key</div>
        <div style={{ flex: 1, maxWidth: 150 }}>Label</div>
        <div style={{ minWidth: 80 }}>Preview</div>
        <div style={{ width: 16 }}>Bg</div>
        <div style={{ width: 50, textAlign: 'center' }}>Default</div>
        <div style={{ width: 50, textAlign: 'center' }}>Terminal</div>
        <div style={{ width: 40, textAlign: 'center' }}>Active</div>
        <div style={{ width: 20 }}></div>
        <div style={{ width: 24 }}></div>
      </div>

      {/* Status rows with drag and drop */}
      {statuses.length === 0 && (
        <Empty
          description="No statuses configured"
          style={{ padding: '40px 0' }}
        />
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={statuses.map((s) => s.id)}
          strategy={verticalListSortingStrategy}
        >
          {statuses.map((status) => (
            <SortableStatusRow
              key={status.id}
              status={status}
              onUpdate={updateStatus}
              onDelete={handleDelete}
            />
          ))}
        </SortableContext>
      </DndContext>

      {/* New status row */}
      <StatusRow
        key="new"
        status={newStatusPlaceholder}
        onUpdate={updateStatus}
        onDelete={handleDelete}
        onCreated={() => {
          queryClient.invalidateQueries({ queryKey: ['statuses'] });
        }}
      />

      <Space direction="vertical" style={{ marginTop: 16 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          <strong>Key:</strong> Internal identifier (lowercase, underscores only). Cannot be changed after creation.
        </Text>
        <Text type="secondary" style={{ fontSize: 12 }}>
          <strong>Default:</strong> New tasks are created with this status.
        </Text>
        <Text type="secondary" style={{ fontSize: 12 }}>
          <strong>Terminal:</strong> Tasks in terminal states (like completed) are considered "done".
        </Text>
      </Space>
    </div>
  );
}
